import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { Modal, Button, Select, DatePicker, Form, Pill } from '@gpstrackit/gtc-react-controls';
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';

import { LogManagerConsole } from 'Core/logManager';
import { reset, emissionCheckSave, closeModal } from './redux/slice';

//Helpers
import { GetFormatForMoment } from 'Core/data/Helpers';

export const EmissionCheckSaveModal = ({ onRefresh }) => {

  //constants
  const { messages } = useIntl();
  const formRef = Form.useForm();
  const dispatch = useDispatch();

  //Redux States
  const user = useSelector(state => state.securityRedux.user);
  const { loading, data, error, result, open, items } = useSelector(state => state.emissionCheckSaveRedux);

  //Local States
  const [formData, setFormData] = useState({});
  const [id, setId] = useState();
  const nextMonth = useMemo(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() + 1, 1)
  }, [])

  //callbackks
  const onClose = useCallback((refresh) => {
    setFormData({})
    setId(null)
    dispatch(closeModal());
    onRefresh && onRefresh(refresh);
    dispatch(reset());
  })

  //effects
  useEffect(() => {
    LogManagerConsole("EmissionCheck", "SaveModal", "data", data, "result", result, "error", error);

    //validate if the data is not empty and the id is not set
    if (data?.id && data?.id != id) {
      setId(data?.id)
      setTimeout(() => {
        formRef.setValues({ unitId: data?.unitId, nextDeadline: data?.nextDeadline })
      }, 500);
    } else {
      setTimeout(() => {
        const newDate = new Date();
        newDate.setDate(newDate.getDate() + 60);
        formRef.setValues({ nextDeadline: newDate })
      }, 500);
    }

    //validate if the result is not empty
    if (result) {
      dispatch(reset({ result: null }));

      NotificationManager.success(
        (data?.id > 0 ? messages['emissionCheck_updatedSuccessfully'] : messages['emissionCheck_addedSuccessfully']),
        messages['success'],
        3000,
        null,
        null,
        'success',
      );

      onClose(true);
    }

    //validate if the error is not empty
    if (error) {
      dispatch(reset({ error: null }));
      NotificationManager.error(
        error?.error || messages['errorMessage'],
        messages['error'],
        3000,
        null,
        null,
        'error',
      );
    }

  }, [data, result, error]);

  return (
    <Modal
      title={data ? messages['emissionCheck_editVehicle'] + ` (${data?.unit?.label})` : messages['emissionCheck_registerVehicle']}
      open={open}
      handleClose={() => onClose(false)}
      position="right"
      size="small"
      loading={loading}
      closeButton={false}
      footer={(
        <div>
          <Button
            variant="secondary"
            onClick={() => onClose(false)}
            style={{ marginRight: "5px" }}
            isDisabled={loading}
          >
            {messages["cancel"]}
          </Button>

          <Button
            onClick={(e) => formRef.submit()}
            variant="primary"
            isDisabled={loading}
          >
            {messages["save"]}
          </Button>
        </div>
      )}
    >
      <div style={{ padding: "15px" }}>
        <Form ref={formRef.ref}
          onValidSubmit={(values) => {
            setFormData({})
            setId(null)
            dispatch(emissionCheckSave({ ...values, id }));
          }}
        >
          {
            data?.id ?
              <Pill
                key='vin'
                item={{ label: messages['emissionCheck_vin'] +": "+ data?.asset?.vin, color: "green" }}
                clickable={false}
                style={{ border: '0', width: '100%', textAlign: 'center', margin: '5px 0 20px 0' }}
                label="pill-label-text"
                size="filled"
              />
              :
              <Form.Item
                name="unitId"
                label={messages['emissionCheck_unit']}
                rules={[{ required: true, message: 'required_field' }]}
              >
                <Select
                  id="unitId"
                  placeholder={messages['emissionCheck_unit']}
                  options={items?.items || []}
                  textField="label"
                  valueField="id"
                  onChange={(value) => {
                    setFormData({ ...formData, unitId: value?.id });
                  }}
                  isSearchable
                />
                <Pill
                  key='vin'
                  textField="label"
                  item={{ label: items?.items?.find((e) => e.id == formData?.unitId)?.vin || messages['emissionCheck_vin'], color: "green" }}
                  clickable={false}
                  style={{ border: '0', width: '100%', textAlign: 'center', marginTop: '10px' }}
                  label="pill-label-text"
                  size="filled"
                />
              </Form.Item>
          }
          <Form.Item
            name="nextDeadline"
            label={messages['emissionCheck_nextDeadline']}
            rules={[{ required: true, message: 'required_field' }]}
          >
            <DatePicker
              id="nextDeadline"
              showTimePicker
              placeholder={messages['emissionCheck_nextDeadline']}
              format={GetFormatForMoment(user.dateformat)}
              minDate={nextMonth}
              onChange={(value) => {
                setFormData({ ...formData, nextDeadline: value });
              }}
            />
          </Form.Item>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
}

export default EmissionCheckSaveModal;